import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import * as Mui from "@mui/material";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import {
  Groups,
  GroupRemove,
  TableView,
  CalendarMonth,
  EventAvailable,
  Autorenew,
  Close,
  Error,
} from "@mui/icons-material";
import moment from "moment";
import {
  themes,
  scrollTop,
  StatusType,
  tableRowSize,
  stickyHeader,
} from "services/constants";
import { useSelector } from "react-redux";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { checkViewerPermission } from "services/utils/HelperFunction";
import { Loader, LoaderWithoutHeight } from "components/Loader";
import BSDateRangePicker from "pages/MyZone/Status/DateRangePicker";
import {
  InfoMessage,
  CustomButtonWithOutLoader,
} from "components/CustomComponents/CustomButton";
import { showToast } from "services/utils/Status";
import {
  operationsDailyPayrunHeader,
  dailyPayrunHeaders,
  dailyPayrunColors,
} from "pages/PayRoll/PayRollHeaders";
import { customGetAPI } from "services/utils/CustomAPI";
import { Typography, Box, Paper } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { useSaveView } from "components/CustomComponents/useSaveView";
import { toFlatDisplayHeaders } from "components/CustomComponents/headerGroupUtils";
import { DateView } from "./DateView";



const REPORT_TYPE_OPTIONS = [
  { label: "Employee Wise", value: "employee_wise" },
  { label: "Department Wise - Summarized", value: "summarized" },
  { label: "Department Wise - Detailed", value: "detailed" },
  { label: "Daily / Hourly Pay Rate", value: "per_day_per_hour" },
];

const SUMMARY_HEADERS = [
  { name: "Department", key: "department_name" },
  { name: "Worked Qty", key: "earning_qty" },
  { name: "Regular Pay", key: "earning_amount" },
  { name: "OT Qty", key: "ot_qty" },
  { name: "OT Pay", key: "ot_amount" },
  { name: "Total", key: "net_salary" },
];

const SUMMARY_EXPORT_HEADERS = SUMMARY_HEADERS.map((header) => ({
  ...header,
  type: header.key === "department_name" ? "string" : "number",
}));

const PER_DAY_PER_HOUR_HEADERS = [
  { name: "Emp ID", key: "user_id" },
  { name: "Emp Name", key: "user_name" },
  { name: "Department", key: "department_name" },
  { name: "Grade", key: "grade_name" },
  { name: "Shift Category", key: "shift_category_name" },
  { name: "Group", key: "group_name" },
  { name: "Per Day Salary", key: "per_day_salary" },
  { name: "Per Hour Salary", key: "per_hour_salary" },
];

const PER_DAY_PER_HOUR_NUMBER_KEYS = ["per_day_salary", "per_hour_salary"];

const PER_DAY_PER_HOUR_EXPORT_HEADERS = PER_DAY_PER_HOUR_HEADERS.map(
  (header) => ({
    ...header,
    type: PER_DAY_PER_HOUR_NUMBER_KEYS.includes(header.key)
      ? "number"
      : "string",
  }),
);

export const DailyPayRun = ({ subModuleId }) => {
  const {
    domain,
    userScreenAccess,
    topbarCompanyId,
    topbarCompanyName,
    userDetails,
  } = useSelector((state) => state.tokenReducer);
  const [loading, setLoading] = useState(false);
  const [showGroupAmount, setShowGroupAmount] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dailyPayrunApi, setDailyPayrunApi] = useState(false);
  const [payscheduleStartDate, setPayScheduleStartDate] = useState("");
  const [payscheduleEndDate, setPayScheduleEndDate] = useState("");
  const [payscheduleName, setPayscheduleName] = useState("");
  const [payscheduleId, setPayscheduleId] = useState(0);
  const [payscheduleDetails, setPayscheduleDetails] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dailyPayRunDetails, setDailyPayrunDetails] = useState([]);
  const [dailyPayRunData, setDailyPayRunData] = useState([]);
  const [perDayPerHourData, setPerDayPerHourData] = useState([]);
  const [perDayPerHourDetails, setPerDayPerHourDetails] = useState([]);
  const [reportType, setReportType] = useState(REPORT_TYPE_OPTIONS[0]);
  const [readyForPayrollWaiting, setReadyForPayrollWaiting] = useState(false);
  const [showIncompleteDataBanner, setShowIncompleteDataBanner] =
    useState(true);
  const readyForPayrollIntervalRef = useRef(null);
  const readyForPayrollElapsedRef = useRef(0);
  const readyForPayrollCancelledRef = useRef(false);
  const containerRef = useRef(null);

  const handleReportTypeChange = (event, data) => {
    if (data) {
      setReportType(data);
      setPage(0);
    }
  };
  let check_access = checkViewerPermission(
    userScreenAccess?.module_details,
    "Payroll Processing",
    "Daily Payrun",
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  useEffect(() => {
    const getPaySchedule = async () => {
      const res = await customGetAPI(
        `${domain}payroll/operations/payrollprocessing/payrollruncycle/list-payschedules/?company_id=${topbarCompanyId}`,
      );
      if (res.ok) {
        setLoading(true);
        setPayscheduleDetails(res?.responseDetails);
        const firstSchedule = res.responseDetails[0];
        setPayscheduleId(firstSchedule?.id);
        setPayScheduleStartDate(
          moment(firstSchedule?.start_date, "DD-MM-YYYY").format("YYYY-MM-DD"),
        );
        setPayScheduleEndDate(
          moment(firstSchedule?.end_date, "DD-MM-YYYY").format("YYYY-MM-DD"),
        );
        setPayscheduleName(firstSchedule?.name);
        setDailyPayrunApi(true);
      }
    };
    getPaySchedule();
  }, [domain, topbarCompanyId]);

  useEffect(() => {
    const getDailyPayrun = async () => {
      const res = await customGetAPI(
        `${domain}payroll/daily_earning_details/?company_id=${topbarCompanyId}&start_date=${payscheduleStartDate}&end_date=${payscheduleEndDate}&payschedule_id=${payscheduleId}`,
      );
      if (res.ok) {
        setDailyPayRunData(res?.responseDetails);
        setLoading(false);
        setDailyPayrunApi(false);
      }
    };
    if (
      dailyPayrunApi &&
      payscheduleStartDate &&
      payscheduleEndDate &&
      payscheduleId
    ) {
      getDailyPayrun();
    }
  }, [
    dailyPayrunApi,
    domain,
    payscheduleEndDate,
    payscheduleId,
    payscheduleStartDate,
    topbarCompanyId,
  ]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      if (reportType?.value === "per_day_per_hour") {
        setPerDayPerHourDetails(filteredData);
      } else {
        setDailyPayrunDetails(filteredData);
      }
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage, reportType],
  );

  useEffect(() => {
    const getPerDayPerHourSalary = async () => {
      setLoading(true);
      const res = await customGetAPI(
        `${domain}payroll/per-day-per-hour-salary/?pay_schedule_id=${payscheduleId}&company_id=${topbarCompanyId}`,
      );
      if (res.ok) {
        setPerDayPerHourData(res?.responseDetails);
      }
      setLoading(false);
    };
    if (reportType?.value === "per_day_per_hour" && payscheduleId) {
      getPerDayPerHourSalary();
    }
  }, [reportType, domain, payscheduleId, topbarCompanyId]);

  useEffect(() => {
    if (topbarCompanyId) {
      setLoading(true);
      setDailyPayrunApi(true);
    }
  }, [topbarCompanyId]);

  useEffect(() => {
    if (readyForPayrollWaiting) {
      setShowIncompleteDataBanner(true);
    }
  }, [readyForPayrollWaiting]);

  const stopReadyForPayrollPolling = useCallback(() => {
    if (readyForPayrollIntervalRef.current) {
      clearInterval(readyForPayrollIntervalRef.current);
      readyForPayrollIntervalRef.current = null;
    }
  }, []);

  const checkReadyForPayrollCount = useCallback(
    async (isPoll) => {
      const response = await customGetAPI(
        `${domain}payroll/earning/ready-for-payroll/counts/?payschedule_id=${payscheduleId}&company_id=${topbarCompanyId}`,
      );

      if (readyForPayrollCancelledRef.current) {
        return;
      }

      const details = response?.responseDetails;
      const count =
        typeof details === "number"
          ? details
          : (details?.attendance_ready_for_payroll_count ?? 0) +
            (details?.holiday_ready_for_payroll_count ?? 0) +
            (details?.post_ot_ready_for_payroll_count ?? 0) +
            (details?.week_off_ready_for_payroll_count ?? 0);

      if (response?.ok && count > 0) {
        if (isPoll) {
          readyForPayrollElapsedRef.current += 5;
        } else {
          readyForPayrollElapsedRef.current = 0;
          showToast(
            "info",
            "Your payroll data is currently being processed. Please wait.",
          );
        }

        setReadyForPayrollWaiting(true);

        if (!readyForPayrollIntervalRef.current) {
          readyForPayrollIntervalRef.current = setInterval(
            () => checkReadyForPayrollCount(true),
            5 * 60 * 1000,
          );
        }
      } else {
        stopReadyForPayrollPolling();
        readyForPayrollElapsedRef.current = 0;
        setReadyForPayrollWaiting(false);
      }
    },
    [domain, payscheduleId, stopReadyForPayrollPolling, topbarCompanyId],
  );

  useEffect(() => {
    if (!payscheduleId) {
      return undefined;
    }

    readyForPayrollCancelledRef.current = false;
    readyForPayrollElapsedRef.current = 0;
    checkReadyForPayrollCount(false);

    return () => {
      readyForPayrollCancelledRef.current = true;
      stopReadyForPayrollPolling();
    };
  }, [payscheduleId, checkReadyForPayrollCount, stopReadyForPayrollPolling]);

  const handleIncompleteDataRefresh = useCallback(() => {
    setLoading(true);
    setDailyPayrunApi(true);
    checkReadyForPayrollCount(false);
  }, [checkReadyForPayrollCount]);

  const round2 = (num) => Number(num.toFixed(2));

  const sumByKey = (data, key) =>
    round2(
      data?.reduce((sum, item) => {
        return sum + (Number(item?.[key]) || 0);
      }, 0) ?? 0,
    );

  const totalOT = sumByKey(dailyPayRunDetails, "overtime_earning");
  const totalShiftEarning = sumByKey(dailyPayRunDetails, "earning_amount");
  const totalPayable = (totalOT + totalShiftEarning).toFixed(2);

  const DEPARTMENT_SUMMABLE_KEYS = [
    "earning_qty",
    "earning_amount",
    "ot_qty",
    "ot_amount",
    "net_salary",
  ];

  // When OT Basis is "Hours", OT Qty is an "HH:MM" duration string, not a
  // plain decimal — Number("01:51") is NaN, so it has to be parsed/summed as
  // time and re-formatted, rather than added like the other summable keys.
  const parseHhMmToMinutes = (value) => {
    const match = /^(\d{1,4}):(\d{1,2})$/.exec(`${value ?? ""}`.trim());
    if (!match) return null;
    return Number(match[1]) * 60 + Number(match[2]);
  };

  const formatMinutesAsHhMm = (totalMinutes) => {
    const safeMinutes = Math.max(0, Math.round(totalMinutes || 0));
    const hours = Math.floor(safeMinutes / 60);
    const minutes = safeMinutes % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  };

  const isHhMmString = (value) =>
    typeof value === "string" && /^\d{1,4}:\d{2}$/.test(value.trim());

  // Worked Qty and OT Qty are each measured in whatever unit their basis
  // field says ("Shift" or "Hours"), so adding a shift-based row to an
  // hours-based row produces a number in no meaningful unit. Sum a qty
  // column only when every row bearing that qty agrees on a basis;
  // otherwise the department total is ambiguous and reports as 0 rather
  // than a misleading sum. When the shared basis is "Hours", the sum is
  // carried out in minutes and re-formatted back to "HH:MM" instead of
  // going through the plain-number path.
  const sumQtyByBasis = (departmentRows, qtyKey, basisKey) => {
    const hasQty = (row) => {
      const basis = `${row?.[basisKey] ?? ""}`.trim().toLowerCase();
      if (basis === "hours")
        return (parseHhMmToMinutes(row?.[qtyKey]) || 0) > 0;
      return Number(row?.[qtyKey]) > 0;
    };

    const bases = new Set(
      departmentRows
        .filter(hasQty)
        .map((row) => `${row?.[basisKey] ?? ""}`.trim().toLowerCase()),
    );

    if (bases.size === 0) return 0;
    if (bases.size > 1) return 0;

    const [basis] = bases;
    if (basis === "hours") {
      const totalMinutes = departmentRows.reduce(
        (sum, row) => sum + (parseHhMmToMinutes(row?.[qtyKey]) || 0),
        0,
      );
      return formatMinutesAsHhMm(totalMinutes);
    }

    return round2(
      departmentRows.reduce(
        (sum, row) => sum + (Number(row?.[qtyKey]) || 0),
        0,
      ),
    );
  };

  const computeDepartmentTotals = (departmentRows) => {
    const totals = departmentRows.reduce((accumulator, row) => {
      DEPARTMENT_SUMMABLE_KEYS.forEach((key) => {
        if (key === "ot_qty" || key === "earning_qty") return;
        accumulator[key] = round2(
          (accumulator[key] || 0) + (Number(row?.[key]) || 0),
        );
      });
      return accumulator;
    }, {});

    totals.earning_qty = sumQtyByBasis(
      departmentRows,
      "earning_qty",
      "earning_basis",
    );
    totals.ot_qty = sumQtyByBasis(departmentRows, "ot_qty", "ot_basis");
    return totals;
  };

  // earning_qty/ot_qty on a total row can be either a plain number (shift
  // basis) or an "HH:MM" string (hours basis) — display it as-is when it's
  // already a time string instead of forcing it through
  // Number().toFixed(2), which would turn "01:51" into "NaN".
  const formatSummableValue = (key, value) => {
    if ((key === "ot_qty" || key === "earning_qty") && isHhMmString(value)) {
      return value;
    }
    return Number(value ?? 0).toFixed(2);
  };

  // Groups the flat per-employee rows by department: a plain banner row with
  // just the department name, then that department's employee rows, then a
  // bold "Subtotal" row — mirroring the Salary Statement report's
  // "Department Wise - Detailed" convention exactly.
  const groupedDailyPayRunDetails = useMemo(() => {
    const grouped = (dailyPayRunDetails || []).reduce((accumulator, row) => {
      const departmentName = row?.department_name || "Unassigned";
      if (!accumulator[departmentName]) {
        accumulator[departmentName] = [];
      }
      accumulator[departmentName].push(row);
      return accumulator;
    }, {});

    const departmentNames = Object.keys(grouped).sort((first, second) =>
      first.localeCompare(second),
    );

    return departmentNames.flatMap((departmentName) => {
      const departmentRows = grouped[departmentName];
      const totals = computeDepartmentTotals(departmentRows);

      return [
        {
          id: `department-header-${departmentName}`,
          rowType: "department_header",
          department_name: departmentName,
          department: departmentName,
        },
        ...departmentRows,
        {
          id: `department-total-${departmentName}`,
          rowType: "department_total",
          department_name: departmentName,
          department: departmentName,
          ...totals,
        },
      ];
    });
  }, [dailyPayRunDetails]);

  // Department Wise - Summarized: one row per department with no individual
  // employee rows, mirroring the Salary Statement report's summarized view.
  const departmentSummaryRows = useMemo(() => {
    const grouped = (dailyPayRunDetails || []).reduce((accumulator, row) => {
      const departmentName = row?.department_name || "Unassigned";
      if (!accumulator[departmentName]) {
        accumulator[departmentName] = [];
      }
      accumulator[departmentName].push(row);
      return accumulator;
    }, {});

    return Object.keys(grouped)
      .sort((first, second) => first.localeCompare(second))
      .map((departmentName) => {
        const departmentRows = grouped[departmentName];
        const totals = computeDepartmentTotals(departmentRows);

        return {
          id: `department-summary-${departmentName}`,
          department_name: departmentName,
          ...totals,
        };
      });
  }, [dailyPayRunDetails]);

  const activeTableRows =
    reportType?.value === "detailed"
      ? groupedDailyPayRunDetails
      : dailyPayRunDetails;

  // The download must reflect whichever report type is on screen.
  // "department_total" rows have their `rowType` stripped before export
  // because the shared export logic special-cases that rowType for Salary
  // Statement's column order (Department first) and would otherwise
  // misplace "Subtotal" into the wrong column for Daily Pay Run's different
  // column order — `__isTotalRow` replaces it as a marker the export
  // headers' customFormatter below keys off instead. "department_header"
  // rows keep their rowType since the shared banner rendering for it
  // already works regardless of column order.
  const activeExportData = useMemo(() => {
    if (reportType?.value === "summarized") {
      return departmentSummaryRows;
    }
    if (reportType?.value === "detailed") {
      return groupedDailyPayRunDetails.map((row) => {
        if (row.rowType !== "department_total") return row;
        const { rowType, ...rest } = row;
        return { ...rest, __isTotalRow: true };
      });
    }
    if (reportType?.value === "per_day_per_hour") {
      return perDayPerHourDetails;
    }
    return dailyPayRunDetails;
  }, [
    reportType,
    departmentSummaryRows,
    groupedDailyPayRunDetails,
    dailyPayRunDetails,
    perDayPerHourDetails,
  ]);

  const cardData = [
    {
      id: 1,
      icon: TableView,
      label: "Regular Pay",
      value: totalShiftEarning.toFixed(2),
      bgColor: "#F0FDFA",
      iconBgColor: "#D1FAF5",
      iconBorderColor: "#0F766E",
      iconColor: "#09BD71",
    },
    {
      id: 2,
      icon: GroupRemove,
      label: "OT Pay",
      value: totalOT.toFixed(2),
      bgColor: "#FFF1F2",
      iconBgColor: "#FFE4E6",
      iconBorderColor: "#DC2626",
      iconColor: "#DC2626",
    },
    {
      id: 3,
      icon: Groups,
      label: "Total",
      value: totalPayable,
      bgColor: "#F5F7FF",
      iconBgColor: "#E0E7FF",
      iconBorderColor: "#1D4ED8",
      iconColor: "#1D4ED8",
    },
    {
      id: 4,
      icon: GroupRemove,
      label: "Cycle StartDate",
      value: moment(payscheduleStartDate).format("DD-MM-YYYY"),
      bgColor: "#FFF1F2",
      iconBgColor: "#FFE4E6",
      iconBorderColor: "#EAAB35",
      iconColor: "#EAAB35",
    },
    {
      id: 5,
      icon: Groups,
      label: "Cycle EndDate",
      value: moment(payscheduleEndDate).format("DD-MM-YYYY"),
      bgColor: "#F5F7FF",
      iconBgColor: "#E0E7FF",
      iconBorderColor: "#7A2DCC",
      iconColor: "#7A2DCC",
    },
  ];

  const handlePaySchedule = (event, data) => {
    if (data) {
      setPayscheduleId(data?.id);
      setPayScheduleStartDate(
        moment(data?.start_date, "DD-MM-YYYY").format("YYYY-MM-DD"),
      );
      setPayScheduleEndDate(
        moment(data?.end_date, "DD-MM-YYYY").format("YYYY-MM-DD"),
      );
      setPayscheduleName(data?.name);
      setDailyPayrunApi(true);
      setLoading(true);
    }
  };

  const handleStartDate = async (startDateValue, endDateValue) => {
    let start_date = moment(
      new Date(startDateValue.split("/").reverse().join("-")),
    ).format("YYYY-MM-DD");
    let end_date = moment(
      new Date(endDateValue.split("/").reverse().join("-")),
    ).format("YYYY-MM-DD");
    setStartDate(start_date);
    setEndDate(end_date);
  };

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Payschedule Name": payscheduleName || "N/A",
      "Regular Pay": totalShiftEarning.toFixed(2),
      "OT Pay": totalOT.toFixed(2),
      Total: totalPayable,
      "Cycle StartDate": payscheduleStartDate
        ? moment(payscheduleStartDate).format("DD-MM-YYYY")
        : "N/A",
      "Cycle EndDate": payscheduleEndDate
        ? moment(payscheduleEndDate).format("DD-MM-YYYY")
        : "N/A",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [
      payscheduleEndDate,
      payscheduleName,
      payscheduleStartDate,
      topbarCompanyName,
      totalOT,
      totalPayable,
      totalShiftEarning,
    ],
  );

  const { visibleHeaders, titleOverride, newButtonComponent } = useSaveView({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "daily_payrun_saved_views",
    fullHeaders: dailyPayrunHeaders,
    screenAccess: check_access,
    onViewChange: () => setPage(0),
  });

  // The filter dropdown/export need each column's type/icon/date-range
  // metadata (only present on the flat operationsDailyPayrunHeader), but the
  // column selection/order/rename must follow the saved view — so merge the
  // two by key rather than picking one source over the other.
  const richHeadersByKey = useMemo(() => {
    const map = new Map();
    operationsDailyPayrunHeader.forEach((header) =>
      map.set(header.key, header),
    );
    return map;
  }, []);

  // Department Wise - Detailed leads with the Department column (matching
  // Salary Statement), but Employee Wise keeps the existing Date-first
  // column order untouched — so this only reorders when it's actually the
  // detailed view.
  const moveDepartmentFirst = (headers) => {
    const departmentIndex = headers.findIndex(
      (header) => header.key === "department_name",
    );
    if (departmentIndex <= 0) return headers;
    const reordered = [...headers];
    const [departmentHeader] = reordered.splice(departmentIndex, 1);
    reordered.unshift(departmentHeader);
    return reordered;
  };

  const isDetailedView = reportType?.value === "detailed";

  const tableHeaders = isDetailedView
    ? moveDepartmentFirst(visibleHeaders)
    : visibleHeaders;

  const exportFilterHeaders = useMemo(() => {
    const flatHeaders = toFlatDisplayHeaders(visibleHeaders).map((header) => ({
      ...richHeadersByKey.get(header.key),
      ...header,
    }));
    return isDetailedView ? moveDepartmentFirst(flatHeaders) : flatHeaders;
  }, [visibleHeaders, richHeadersByKey, isDetailedView]);

  // On a total row, only Department, "Subtotal" under Emp Name, and the
  // summed columns mean anything — every other column blanks out instead of
  // falling through to the export's default "N/A" for a missing value. On a
  // normal row, Department blanks out instead of "N/A" too, since
  // "Unassigned" already covers the on-screen meaning of a missing
  // department and "N/A" only added noise.
  const detailedExportHeaders = useMemo(
    () =>
      exportFilterHeaders.map((header) => ({
        ...header,
        customFormatter: (row) => {
          if (row?.__isTotalRow) {
            if (header.key === "department_name") {
              return row.department_name || "";
            }
            if (header.key === "employee_name") {
              return "Subtotal";
            }
            if (DEPARTMENT_SUMMABLE_KEYS.includes(header.key)) {
              return formatSummableValue(header.key, row?.[header.key]);
            }
            return "";
          }

          if (header.key === "department_name") {
            return row?.department_name || "";
          }

          const value = row?.[header.key];
          return value === null || value === undefined || value === ""
            ? "N/A"
            : value;
        },
      })),
    [exportFilterHeaders],
  );

  const activeExportHeaders =
    reportType?.value === "summarized"
      ? SUMMARY_EXPORT_HEADERS
      : reportType?.value === "per_day_per_hour"
        ? PER_DAY_PER_HOUR_EXPORT_HEADERS
        : detailedExportHeaders;

  return (
    <React.Fragment>
      <Box>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          {/* Left content */}
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              {showGroupAmount ? "Daywise payrun" : "Daily Payrun"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review and finalize employee payments for the day.
            </Typography>
          </Box>
          {/* Right textbox */}
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              {/* Icon */}
              <Mui.Stack sx={{ paddingTop: !showGroupAmount ? 3 : 0 }}>
                <Mui.Tooltip
                  title={
                    showGroupAmount
                      ? "Change to Daily payrun"
                      : "Change to Daywise payrun"
                  }
                  arrow
                  placement="top"
                >
                  <Mui.IconButton
                    onClick={() => setShowGroupAmount(!showGroupAmount)}
                    sx={{
                      color: "text.secondary",
                      transition: "color 0.2s ease, background-color 0.2s ease",
                      "&:hover": {
                        color: "primary.main", // icon color on hover
                        backgroundColor: "action.hover", // subtle hover background
                      },
                    }}
                  >
                    {showGroupAmount ? <EventAvailable /> : <CalendarMonth />}
                  </Mui.IconButton>
                </Mui.Tooltip>
              </Mui.Stack>
              {/* Label + Autocomplete */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  minWidth: 350,
                  maxWidth: 400,
                }}
              >
                {!showGroupAmount ? (
                  <>
                    <Mui.Typography
                      sx={{
                        whiteSpace: "nowrap",
                        fontWeight: 500,
                      }}
                    >
                      Pay Schedule
                    </Mui.Typography>

                    <CustomAutoComplete
                      dataList={payscheduleDetails.map((obj) => ({
                        ...obj,
                        label: obj.name,
                      }))}
                      selectedValue={payscheduleName}
                      updateDataFunction={handlePaySchedule}
                      placeHolder="Please select the Payschedule"
                      sx={{ minWidth: 300 }}
                    />
                  </>
                ) : (
                  <BSDateRangePicker
                    handleStartDate={handleStartDate}
                    showToday={false}
                    triggerDate={true}
                  />
                )}
              </Box>
              {!showGroupAmount && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    minWidth: 300,
                    maxWidth: 350,
                  }}
                >
                  <Mui.Typography
                    sx={{
                      whiteSpace: "nowrap",
                      fontWeight: 500,
                    }}
                  >
                    Report Type
                  </Mui.Typography>

                  <CustomAutoComplete
                    dataList={REPORT_TYPE_OPTIONS}
                    selectedValue={reportType?.label}
                    updateDataFunction={handleReportTypeChange}
                    placeHolder="Please select the Report Type"
                    sx={{ minWidth: 260 }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        {readyForPayrollWaiting && showIncompleteDataBanner && (
          <Mui.Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
              mb: 2,
              px: 2,
              py: 1.5,
              borderRadius: 2,
              backgroundColor: "#FFF8E7",
              border: "1px solid #FBE3B5",
            }}
          >
            <Mui.Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Error sx={{ color: "#F59E0B", mt: "2px" }} fontSize="small" />
              <Mui.Box>
                <Mui.Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", color: "text.primary" }}
                >
                  Payroll data may be incomplete
                </Mui.Typography>
                <Mui.Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Some attendance data may still be processing. Refresh the
                  page to check for the latest payroll data.
                </Mui.Typography>
              </Mui.Box>
            </Mui.Box>
            <Mui.Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CustomButtonWithOutLoader
                actionName="Refresh"
                AddIcon={<Autorenew fontSize="small" />}
                actionFuntion={handleIncompleteDataRefresh}
              />
              <Mui.IconButton
                size="small"
                onClick={() => setShowIncompleteDataBanner(false)}
              >
                <Close fontSize="small" />
              </Mui.IconButton>
            </Mui.Box>
          </Mui.Box>
        )}
        {!showGroupAmount ? (
            <Paper
              sx={{
                width: "100%",
                height: "78vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden", // important
              }}
            >
              <CustomFilter
                title="Daily Payrun Records"
                titleOverride={titleOverride}
                filterHeaders={activeExportHeaders}
                data={
                  reportType?.value === "per_day_per_hour"
                    ? perDayPerHourData
                    : dailyPayRunData
                }
                filteredData={activeExportData}
                onFilteredDataChange={handleFilteredDataChange}
                screenAccess={check_access}
                newButtonComponent={newButtonComponent}
                downloadFormats={["excel", "pdf", "csv"]}
                includeMetadata={true}
                metadata={reportMetadata}
              />
              {reportType?.value !== "per_day_per_hour" && (
                <Box>
                  <Mui.Box sx={{ flexGrow: 1, overflow: "auto", pt: 0, mb: 2 }}>
                    <Mui.Box
                      sx={{
                        paddingLeft: 3,
                        paddingRight: 3,
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        gap: 4,
                        flexWrap: "wrap", // ⭐ KEY FIX
                        alignItems: "flex-start",
                      }}
                    >
                      {cardData.map((item) => {
                        const IconComponent = item.icon;
                        return (
                          <Mui.Card
                            key={item.id}
                            sx={{
                              flex: "0 0 200px",
                              width: 200,
                              height: 123,
                              minWidth: 200, // optional safety
                              minHeight: 123,
                              bgcolor: themes?.whiteColor,
                              borderRadius: 1,
                              boxShadow: "0px 4px 4px rgba(0,0,0,0.25)",
                              border: `0.5px solid ${item.iconBorderColor}`,
                            }}
                          >
                            <Mui.CardContent
                              sx={{
                                p: 2,
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                              }}
                            >
                              <Mui.Box
                                sx={{
                                  width: 35,
                                  height: 35,
                                  bgcolor: item.iconBgColor,
                                  border: `0.5px solid ${item.iconBorderColor}`,
                                  borderRadius: 1,
                                  boxShadow: "0px 1px 2px rgba(0,0,0,0.25)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  mb: 1.5,
                                }}
                              >
                                <IconComponent
                                  width={16}
                                  height={16}
                                  style={{ color: item.iconColor, padding: 2 }}
                                />
                              </Mui.Box>
                              <Mui.Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 600,
                                  mb: "auto",
                                }}
                              >
                                {item.label}
                              </Mui.Typography>
                              <Mui.Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  color: "#32325D",
                                }}
                              >
                                {item.value}
                              </Mui.Typography>
                            </Mui.CardContent>
                          </Mui.Card>
                        );
                      })}
                    </Mui.Box>
                  </Mui.Box>
                </Box>
              )}
              <Mui.Grid
                sx={{
                  ml: 2,
                  mr: 2,
                  mt: reportType?.value === "per_day_per_hour" ? 2 : 0,
                  flex: 1, // ⭐ take remaining height
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Mui.TableContainer
                  ref={containerRef}
                  sx={{
                    flex: 1,
                    overflow: "auto",
                  }}
                >
                  {loading ? (
                    <LoaderWithoutHeight />
                  ) : reportType?.value === "summarized" ? (
                  <Mui.Table className="border">
                    <Mui.TableHead sx={stickyHeader}>
                      <Mui.TableRow sx={tableRowSize}>
                        {SUMMARY_HEADERS.map((header) => (
                          <Mui.TableCell
                            key={header.key}
                            align="center"
                            sx={{
                              color: themes.headLine,
                              fontWeight: "bold",
                              border: "1px solid #ddd",
                            }}
                          >
                            {header.name}
                          </Mui.TableCell>
                        ))}
                      </Mui.TableRow>
                    </Mui.TableHead>
                    <Mui.TableBody>
                      {departmentSummaryRows?.length === 0 ? (
                        <TableEmptyState dataLength={dailyPayRunData} />
                      ) : (
                        departmentSummaryRows
                          ?.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                          ?.map((row) => (
                            <Mui.TableRow key={row.id} sx={tableRowSize}>
                              {SUMMARY_HEADERS.map((header) => (
                                <Mui.TableCell
                                  key={header.key}
                                  align="center"
                                  sx={{ border: "1px solid #ddd" }}
                                >
                                  {header.key === "department_name"
                                    ? row.department_name
                                    : formatSummableValue(
                                        header.key,
                                        row?.[header.key],
                                      )}
                                </Mui.TableCell>
                              ))}
                            </Mui.TableRow>
                          ))
                      )}
                    </Mui.TableBody>
                  </Mui.Table>
                  ) : reportType?.value === "per_day_per_hour" ? (
                  <Mui.Table className="border">
                    <Mui.TableHead sx={stickyHeader}>
                      <Mui.TableRow sx={tableRowSize}>
                        {PER_DAY_PER_HOUR_HEADERS.map((header) => (
                          <Mui.TableCell
                            key={header.key}
                            align="center"
                            sx={{
                              color: themes.headLine,
                              fontWeight: "bold",
                              border: "1px solid #ddd",
                            }}
                          >
                            {header.name}
                          </Mui.TableCell>
                        ))}
                      </Mui.TableRow>
                    </Mui.TableHead>
                    <Mui.TableBody>
                      {perDayPerHourDetails?.length === 0 ? (
                        <InfoMessage dataLength={perDayPerHourData} />
                      ) : (
                        perDayPerHourDetails
                          ?.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                          ?.map((row) => (
                            <Mui.TableRow key={row.id} sx={tableRowSize}>
                              {PER_DAY_PER_HOUR_HEADERS.map((header) => (
                                <Mui.TableCell
                                  key={header.key}
                                  align="center"
                                  sx={{ border: "1px solid #ddd" }}
                                >
                                  {row?.[header.key] ?? "-"}
                                </Mui.TableCell>
                              ))}
                            </Mui.TableRow>
                          ))
                      )}
                    </Mui.TableBody>
                  </Mui.Table>
                  ) : (
                  <Mui.Table className="border">
                    <Mui.TableHead sx={stickyHeader}>
                      <Mui.TableRow sx={tableRowSize}>
                        {tableHeaders.map((header) => (
                          <Mui.TableCell
                            key={header.name}
                            align="center"
                            colSpan={
                              header.children ? header.children.length : 1
                            }
                            rowSpan={header.children ? 1 : 2}
                            sx={{
                              color: themes.headLine,
                              fontWeight: "bold",
                              border: "1px solid #ddd",
                            }}
                          >
                            {header.name}
                          </Mui.TableCell>
                        ))}
                      </Mui.TableRow>
                      <Mui.TableRow sx={tableRowSize}>
                        {tableHeaders
                          .filter((header) => header.children)
                          .flatMap((header) =>
                            header.children.map((child) => (
                              <Mui.TableCell
                                key={child.key}
                                align="center"
                                sx={{
                                  color: themes.headLine,
                                  fontWeight: "bold",
                                  border: "1px solid #ddd",
                                }}
                              >
                                {child.name}
                              </Mui.TableCell>
                            )),
                          )}
                      </Mui.TableRow>
                    </Mui.TableHead>
                    <Mui.TableBody>
                      {dailyPayRunDetails?.length === 0 ? (
                        <TableEmptyState dataLength={dailyPayRunData} />
                      ) : (
                        activeTableRows
                          ?.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                          ?.map((row, index) =>
                            row.rowType === "department_header" ? (
                              <Mui.TableRow key={row.id}>
                                <Mui.TableCell
                                  colSpan={tableHeaders.reduce(
                                    (count, header) =>
                                      count +
                                      (header.children
                                        ? header.children.length
                                        : 1),
                                    0,
                                  )}
                                  sx={{
                                    fontWeight: 700,
                                    color: themes.headLine,
                                    backgroundColor: "#eef5ff",
                                    border: "1px solid #ddd",
                                  }}
                                >
                                  {row.department_name}
                                </Mui.TableCell>
                              </Mui.TableRow>
                            ) : row.rowType === "department_total" ? (
                              <Mui.TableRow
                                key={row.id}
                                sx={{
                                  backgroundColor: "#f8fafc",
                                  "& .MuiTableCell-root": {
                                    fontWeight: 700,
                                  },
                                }}
                              >
                                {tableHeaders.flatMap((header) =>
                                  header.children
                                    ? header.children.map((child) => (
                                        <Mui.TableCell
                                          key={child.key}
                                          align="center"
                                          sx={{ border: "1px solid #ddd" }}
                                        >
                                          {DEPARTMENT_SUMMABLE_KEYS.includes(
                                            child.key,
                                          )
                                            ? formatSummableValue(
                                                child.key,
                                                row[child.key],
                                              )
                                            : ""}
                                        </Mui.TableCell>
                                      ))
                                    : [
                                        <Mui.TableCell
                                          key={header.key}
                                          align="center"
                                          sx={{ border: "1px solid #ddd" }}
                                        >
                                          {header.key === "department_name"
                                            ? row.department_name
                                            : header.key === "employee_name"
                                              ? "Subtotal"
                                              : DEPARTMENT_SUMMABLE_KEYS.includes(
                                                    header.key,
                                                  )
                                                ? formatSummableValue(
                                                    header.key,
                                                    row[header.key],
                                                  )
                                                : ""}
                                        </Mui.TableCell>,
                                      ],
                                )}
                              </Mui.TableRow>
                            ) : (
                              <Mui.TableRow
                                key={row.id ?? `${row.employee_code}-${row.date}-${index}`}
                                sx={tableRowSize}
                              >
                                {tableHeaders.flatMap((header) =>
                                  header.children
                                    ? header.children.map((child) => (
                                        <Mui.TableCell
                                          key={child.key}
                                          align="center"
                                          sx={{ border: "1px solid #ddd" }}
                                        >
                                          {row[child.key] ?? "-"}
                                        </Mui.TableCell>
                                      ))
                                    : [
                                        <Mui.TableCell
                                          key={header.key}
                                          align="center"
                                          sx={{ border: "1px solid #ddd" }}
                                        >
                                          {header.key === "day_type" ? (
                                            <span
                                              style={StatusType(
                                                row.day_type,
                                                dailyPayrunColors,
                                              )}
                                            >
                                              {row.day_type}
                                            </span>
                                          ) : (
                                            (row[header.key] ?? "-")
                                          )}
                                        </Mui.TableCell>,
                                      ],
                                )}
                              </Mui.TableRow>
                            ),
                          )
                      )}
                    </Mui.TableBody>
                  </Mui.Table>
                  )}
                </Mui.TableContainer>
                <Box sx={{ mt: 1 }}>
                  {(reportType?.value === "summarized"
                    ? departmentSummaryRows?.length
                    : reportType?.value === "per_day_per_hour"
                      ? perDayPerHourDetails?.length
                      : activeTableRows?.length) > 25 && (
                    <Mui.TablePagination
                      className="custom-pagination"
                      component="div"
                      rowsPerPageOptions={[25, 50, 75, 100]}
                      count={
                        reportType?.value === "summarized"
                          ? departmentSummaryRows?.length
                          : reportType?.value === "per_day_per_hour"
                            ? perDayPerHourDetails?.length
                            : activeTableRows?.length
                      }
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  )}
                </Box>
              </Mui.Grid>
            </Paper>
        ) : (
          <DateView
            startDate={startDate}
            endDate={endDate}
            subModuleId={subModuleId}
          />
        )}
      </Box>
    </React.Fragment>
  );
};
